import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth';

interface ErrorLike {
  message?: string;
  statusCode?: number;
  statusText?: string;
}

interface RouteErrorPageProps {
  error: unknown;
  onRetry?: () => void;
}

function isErrorLike(error: unknown): error is ErrorLike {
  return typeof error === 'object' && error !== null;
}

function getErrorDescription(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (isErrorLike(error)) {
    return error.message ?? error.statusText ?? 'Something went wrong while loading this page.';
  }
  return 'Something went wrong while loading this page.';
}

export function RouteErrorPage({ error, onRetry }: RouteErrorPageProps) {
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  const status = isErrorLike(error) ? error.statusCode : undefined;
  const statusText = isErrorLike(error) ? error.statusText : undefined;

  const title = status ? `${status} ${statusText ?? 'Error'}` : 'Unexpected application error';
  const description = getErrorDescription(error);

  return (
    <div className="bg-background relative flex min-h-screen items-center justify-center p-4">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_15%,color-mix(in_oklch,var(--destructive)_12%,transparent),transparent_45%),radial-gradient(circle_at_85%_100%,color-mix(in_oklch,var(--accent)_12%,transparent),transparent_50%)]" />
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to={isAuthed ? '/dashboard' : '/login'}>
              {isAuthed ? 'Go to dashboard' : 'Go to login'}
            </Link>
          </Button>
          <Button type="button" variant="outline" onClick={onRetry ?? (() => window.location.reload())}>
            Try again
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
